// =============================================================================
// pages/CAReviews.jsx — CA views all reviews received and responds to them
// =============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import '../styles/CAReviews.css';

function Stars({ rating }) {
  return (
    <span className="crv-stars">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={i <= rating ? 'crv-star crv-star--filled' : 'crv-star'}>★</span>
      ))}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ReviewCard({ review, onResponseSaved }) {
  const [editing,   setEditing]   = useState(false);
  const [text,      setText]      = useState(review.ca_response || '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const save = async () => {
    if (!text.trim()) return setError('Response cannot be empty');
    setSaving(true); setError('');
    try {
      const updated = await api.updateCAResponse(review.id, text.trim());
      onResponseSaved(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="crv-card">
      <div className="crv-card-top">
        <div>
          <div className="crv-reviewer">{review.reviewer_name || review.reviewer_email}</div>
          <div className="crv-date">{fmtDate(review.updated_at || review.created_at)}</div>
        </div>
        <Stars rating={review.rating} />
      </div>

      {review.review_text && (
        <div className="crv-review-text">"{review.review_text}"</div>
      )}

      <div className="crv-response-section">
        {editing ? (
          <div className="crv-response-edit">
            <label className="crv-response-label">Your Response</label>
            <textarea
              className="crv-textarea"
              rows={3}
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Write your response…"
              autoFocus
            />
            {error && <div className="crv-error">{error}</div>}
            <div className="crv-response-actions">
              <button className="crv-btn crv-btn--secondary" onClick={() => { setEditing(false); setText(review.ca_response || ''); }} disabled={saving}>
                Cancel
              </button>
              <button className="crv-btn crv-btn--primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Response'}
              </button>
            </div>
          </div>
        ) : review.ca_response ? (
          <div className="crv-response-display">
            <div className="crv-response-label">Your Response <span className="crv-responded-date">· {fmtDate(review.ca_responded_at)}</span></div>
            <div className="crv-response-text">{review.ca_response}</div>
            <button className="crv-btn crv-btn--ghost" onClick={() => setEditing(true)}>Edit Response</button>
          </div>
        ) : (
          <button className="crv-btn crv-btn--outline" onClick={() => setEditing(true)}>
            Respond to Review
          </button>
        )}
      </div>
    </div>
  );
}

export default function CAReviews({ userRole }) {
  const [reviews,  setReviews]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await api.getReceivedReviews();
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResponseSaved = (updated) => {
    setReviews(r => r.map(x => x.id === updated.id ? { ...x, ...updated } : x));
  };

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (!userRole?.isCA && !userRole?.isAdmin) {
    return <div className="crv-wrap"><p className="crv-error">CA access required.</p></div>;
  }

  return (
    <div className="crv-wrap">
      <div className="crv-header">
        <div>
          <h2 className="crv-title">My Reviews</h2>
          <p className="crv-subtitle">Ratings and feedback from your clients.</p>
        </div>
        {avg && (
          <div className="crv-avg">
            <span className="crv-avg-num">{avg}</span>
            <span className="crv-avg-star">★</span>
            <span className="crv-avg-count">({reviews.length})</span>
          </div>
        )}
      </div>

      {error && <div className="crv-error crv-error--block">{error}</div>}

      {loading ? (
        <div className="crv-loading">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="crv-empty">No reviews yet. Reviews appear here once clients rate you after an engagement.</div>
      ) : (
        <div className="crv-list">
          {reviews.map(r => (
            <ReviewCard key={r.id} review={r} onResponseSaved={handleResponseSaved} />
          ))}
        </div>
      )}
    </div>
  );
}
